import React from 'react';
import { css } from '@emotion/react';
import { Trans } from '@lingui/macro';
import { HasJoined, PlayerMode } from '$/components/WebinarPlayer/types';
import { useWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { useThemeContext } from '../ui/ThemeContext';
import tw from 'twin.macro';

interface ErrorProps {
  error: string | null | undefined;
  errorCode?: number;
  mode?: PlayerMode;
  className?: string;
}

const ResolutionInstructions: React.FC = () => (
  <div className='flex justify-center'>
    <div className='text-left mt-1_5e'>
      <Trans>To resolve, try the following</Trans>:
      <ol
        className='list-inside mt-1e'
        css={css`
          list-style: decimal;
        `}
      >
        <li className='cursor-pointer' onClick={(): void => document.location.reload()}>
          <span className='underline'>
            <Trans>Refresh your browser</Trans>
          </span>
        </li>
        <li>
          <Trans>Use an incognito browser window</Trans>
        </li>
        <li>
          <Trans>Use a different browser or device</Trans>
        </li>
      </ol>
      <div className='mt-1e'>
        <Trans>If the problem persists, please contact us.</Trans>
      </div>
    </div>
  </div>
);

export const OverVideoErrorBox: React.FC<ErrorProps> = ({ error, errorCode, mode, className }) => {
  const { hasJoined, ewebinar, currentRoom } = useWebinarPlayerContext();
  const etheme = useThemeContext();

  const instructions = React.useMemo(() => {
    if (errorCode && [403, 404].includes(errorCode)) {
      return null;
    }

    if (mode === PlayerMode.edit) {
      return (
        <div
          className='mt-1_5e cursor-pointer underline'
          onClick={(): void => document.location.reload()}
        >
          Please refresh or retry your upload
        </div>
      );
    }

    return <ResolutionInstructions />;
  }, [errorCode, mode]);

  if (
    !error ||
    hasJoined === HasJoined.Joining ||
    !ewebinar.uploadStatus?.done ||
    !currentRoom.caps.hasVideoPlayer
  ) {
    return null;
  }

  return (
    <div
      className={className}
      css={[
        tw`absolute h-full md:w-full z-player-error mx-auto flex text-center items-center justify-center p-1e md:p-0 pointer-events-none`,
      ]}
    >
      <div
        className='aspect-scale md:text-lg max-w-screen md:w-40e p-2e rounded-xl'
        css={css`
          line-height: 2rem;
          background: ${etheme.player.error.bg};
          color: ${etheme.player.error.fg};
          backdrop-filter: blur(3px);
        `}
      >
        {error}
        {instructions}
      </div>
    </div>
  );
};

export const JoiningErrorBox: React.FC<ErrorProps> = ({ error, errorCode }) => {
  const { hasJoined } = useWebinarPlayerContext();
  const etheme = useThemeContext();

  const instructions = React.useMemo(() => {
    if (errorCode && [403, 404].includes(errorCode)) return null;
    return <ResolutionInstructions />;
  }, [errorCode]);

  if (!error || hasJoined !== HasJoined.Joining) {
    return null;
  }

  return (
    <div
      className='md:text-lg max-w-screen md:w-40e p-1_5e rounded-xl mt-3e text-center cursor-pointer'
      css={css`
        line-height: 2rem;
        background: ${etheme.player.error.bg};
        color: ${etheme.player.error.fg};
        backdrop-filter: blur(3px);
      `}
    >
      {error}
      {instructions}
    </div>
  );
};
